import type { RefObject } from "react";
import { SpeakerIcon } from "./SpeakerIcon";

type Props = {
  direction: string;
  word: string;
  wordLang: string;
  /** BCP-47 tag of the answers, set on the input so the keyboard can follow. */
  answerLang: string;
  /** Placeholder telling the learner what to type. */
  answerLabel: string;
  typed: string;
  onTyped: (value: string) => void;
  revealed: boolean;
  wasCorrect: boolean | null;
  mainAnswer: string;
  alts: string[];
  /**
   * Receives the native keypress when Enter triggered it, so the app can tell
   * that this exact event has already been consumed.
   */
  onCheck: (nativeEvent?: KeyboardEvent) => void;
  onNext: () => void;
  onGiveUp: () => void;
  inputRef: RefObject<HTMLInputElement | null>;
  /** Absent when the device cannot speak, so no dead control is shown. */
  onSpeak?: () => void;
};

export function AnswerCard({
  direction,
  word,
  wordLang,
  answerLang,
  answerLabel,
  typed,
  onTyped,
  revealed,
  wasCorrect,
  mainAnswer,
  alts,
  onCheck,
  onNext,
  onGiveUp,
  inputRef,
  onSpeak,
}: Props) {
  const inputClass = [
    "input",
    revealed && wasCorrect === true ? "input-correct" : "",
    revealed && wasCorrect === false ? "input-wrong" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <div className="card">
        <div className="direction">{direction}</div>
        <div className="word" lang={wordLang}>
          {word}
        </div>

        <div className="answer-row">
          <input
            ref={inputRef}
            className={inputClass}
            value={typed}
            onChange={(e) => onTyped(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              e.preventDefault();
              onCheck(e.nativeEvent);
            }}
            disabled={revealed}
            lang={answerLang}
            placeholder={answerLabel}
            aria-label="Your answer"
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            autoFocus
          />
          {revealed ? (
            <button type="button" className="btn btn-primary" onClick={onNext}>
              Next
            </button>
          ) : (
            <button type="button" className="btn btn-primary" onClick={() => onCheck()}>
              Check
            </button>
          )}
        </div>

        {revealed && wasCorrect === true && (
          <div className="feedback-correct" role="status">
            <div className="feedback-row">
              <span>
                <b>Correct!</b>
                {alts.length > 0 && <span> Also accepted: {alts.join(", ")}</span>}
              </span>
              {onSpeak && <ReplayButton word={mainAnswer} onClick={onSpeak} />}
            </div>
          </div>
        )}

        {revealed && wasCorrect === false && (
          <div className="feedback-wrong" role="status">
            <div className="feedback-wrong-label">Not quite — the answer was</div>
            <div className="feedback-row">
              <div className="feedback-wrong-answer">{mainAnswer}</div>
              {onSpeak && <ReplayButton word={mainAnswer} onClick={onSpeak} />}
            </div>
            {alts.length > 0 && <div className="feedback-wrong-alts">also: {alts.join(", ")}</div>}
          </div>
        )}

        {!revealed && (
          <div>
            <button type="button" className="btn-link" onClick={onGiveUp}>
              I don&rsquo;t know
            </button>
          </div>
        )}
      </div>

      <div className="hint">Enter to check · Enter again for the next word</div>
    </>
  );
}

function ReplayButton({ word, onClick }: { word: string; onClick: () => void }) {
  return (
    <button
      type="button"
      className="btn-icon"
      onClick={onClick}
      aria-label={`Hear "${word}" again`}
      title="Hear it again"
    >
      <SpeakerIcon />
    </button>
  );
}
